/**
 * 词卡口语评估 - 简化版
 * 只返回：通过/未通过 + 熟练度等级
 */

import { callGeminiAudio } from './genaiClient';

export interface VocabSpeakingResult {
  passed: boolean;           // 是否通过
  level?: 'perfect' | 'good' | 'acceptable' | 'poor';  // 熟练度
  transcript?: string;       // 转写文本（用于展示）
  error?: string;
}

/**
 * 评估词卡口语表现
 * @param audioBlob 录音音频
 * @param targetPhrase 目标短语/词
 * @param cnHint 中文提示（用于LLM理解上下文）
 */
export async function evaluateVocabSpeaking(
  audioBlob: Blob,
  targetPhrase: string,
  cnHint?: string
): Promise<VocabSpeakingResult> {
  const prompt = `You are evaluating a vocabulary practice session.

Target phrase: "${targetPhrase}"
${cnHint ? `Chinese meaning: ${cnHint}` : ''}

Instructions:
1. Transcribe exactly what the user said in English.
2. Determine if their answer matches the target phrase (allow minor variations like articles/word order).
3. Rate fluency: perfect (完美) / good (良好) / acceptable (可接受) / poor (需改进).

Return pure JSON (no markdown):
{
  "transcript": "what user said",
  "passed": true/false,
  "level": "perfect/good/acceptable/poor"
}`;

  try {
    const apiKey = import.meta.env.VITE_GOOGLE_API_KEY || '';
    if (!apiKey) {
      throw new Error('GOOGLE_API_KEY not configured');
    }

    const responseText = await callGeminiAudio({
      apiKey,
      blob: audioBlob,
      prompt,
    });
    
    // 解析 JSON 响应
    const parsed = JSON.parse(responseText);
    const result: VocabSpeakingResult = {
      passed: Boolean(parsed.passed),
      level: parsed.level as VocabSpeakingResult['level'],
      transcript: parsed.transcript,
    };

    return result;
  } catch (error) {
    console.error('[vocabEvaluator] evaluation failed:', error);
    return {
      passed: false,
      error: error instanceof Error ? error.message : '评估失败',
    };
  }
}
